import { useViewportRect } from '@src/hooks/useViewportRect';
import { toLocalLength, toNormalizedPoint } from '@src/utility/frameGeometry';
import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { CLIP_PREVIEW_Z } from '../clipPreviewZ';
import { strokePath } from './strokePath';

import type { Stroke, StrokeTool } from '@src/types.ts/comment';
import type { FrameRect, NormalizedPoint } from '@src/utility/frameGeometry';

type Props = {
  /**
   * The aspect-ratio-shaped player element. Its bounding rect *is* the video
   * frame, which is why the overscanned iframe inside is never measured.
   */
  playerElement: HTMLElement | null | undefined;
  /**
   * The armed tool, or `null` for display-only. `null` is also how a saved
   * drawing is shown read-only, so there is no separate mode to maintain.
   */
  tool: StrokeTool | null;
  /** Ink for marks drawn from now on; existing strokes keep their own. */
  color: string;
  /** Width of new marks, as a fraction of frame height. */
  width: number;
  strokes: Stroke[];
  onStrokesChange?: (strokes: Stroke[]) => void;
  /**
   * Fired once when a mark begins, before any point is recorded. The host
   * pauses playback here: a mark belongs to one frame, so it cannot be drawn
   * on a moving picture.
   */
  onDrawStart?: () => void;
};

/**
 * The surface a reviewer draws on, over the clip preview player (PLAT-11288).
 *
 * **Portaled to `document.body`, never mounted inside the canvas container.**
 * `CreatomateClipPreview` states the rule in its own header: an overlay in
 * there intercepts the pointer events the interactive canvas needs for
 * drag/resize. So this follows `SafeZoneOverlay` — portal out, position fixed
 * on the measured player rect, and stay `pointer-events: none` unless a tool is
 * armed. With no tool armed the canvas behaves exactly as it does today.
 *
 * **Coordinates are stored frame-relative, never in pixels**, so a mark drawn
 * at one window size lands in the same place at another and on the public share
 * page. See `@src/utility/frameGeometry`.
 *
 * Substrate-agnostic on purpose: it measures whichever element it is given, so
 * it works over the live Creatomate iframe and the rendered Mux player alike.
 */
export const ClipAnnotationLayer: React.FC<Props> = ({
  playerElement,
  tool,
  color,
  width,
  strokes,
  onStrokesChange,
  onDrawStart,
}) => {
  const rect = useViewportRect(playerElement);
  const [draft, setDraft] = useState<Stroke | null>(null);
  // The in-progress stroke is mirrored in a ref so the move/up handlers read
  // the live value rather than the one their closure captured on pointerdown.
  const draftRef = useRef<Stroke | null>(null);
  const rectRef = useRef<FrameRect | null>(null);
  rectRef.current = rect;

  const setDraftStroke = useCallback((next: Stroke | null) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      if (!tool || !rectRef.current || event.button !== 0) return;

      // Take the drag even if the pointer leaves the frame — people overshoot,
      // and the coordinate conversion clamps to the frame edge.
      event.currentTarget.setPointerCapture(event.pointerId);
      // Suppress the default focus shift. Blurring the renderer iframe fires
      // its deactivation reaction, which can refresh the timeline and revert an
      // uncommitted canvas edit (CLAUDE.md pitfall 10). We never call `.focus()`
      // ourselves; this stops the browser doing it on our behalf.
      event.preventDefault();

      onDrawStart?.();

      const point = toNormalizedPoint(
        event.clientX,
        event.clientY,
        rectRef.current,
      );
      setDraftStroke({
        tool,
        color,
        width,
        // A shape is defined by two ends, so it starts with both at the press
        // point and the drag moves the second. Freehand accumulates instead,
        // and a duplicated first point would be dead weight in every stroke.
        points: tool === 'freehand' ? [point] : [point, point],
      });
    },
    [tool, color, width, onDrawStart, setDraftStroke],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      const current = draftRef.current;
      if (!current || !rectRef.current) return;

      const point = toNormalizedPoint(
        event.clientX,
        event.clientY,
        rectRef.current,
      );
      setDraftStroke({
        ...current,
        // Freehand accumulates the whole path; the shape tools are defined by
        // their two ends, so dragging only moves the second one.
        points:
          current.tool === 'freehand'
            ? [...current.points, point]
            : [current.points[0], point],
      });
    },
    [setDraftStroke],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      const current = draftRef.current;
      setDraftStroke(null);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (!current) return;

      // A tap without movement is not a mark. Both points are the pointerdown
      // position, so committing it would leave an invisible zero-length stroke
      // that still counts as "this comment has a drawing".
      const [start, end] = [current.points[0], current.points.at(-1)];
      if (end && isSamePoint(start, end)) return;

      onStrokesChange?.([...strokes, current]);
    },
    [strokes, onStrokesChange, setDraftStroke],
  );

  if (!rect) return null;

  const visible = draft ? [...strokes, draft] : strokes;
  if (visible.length === 0 && !tool) return null;

  return createPortal(
    <OverlayRoot
      data-testid="clip-annotation-layer"
      data-armed={tool ?? undefined}
      style={{
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      }}
    >
      <Surface
        data-testid="clip-annotation-surface"
        $armed={!!tool}
        $cursor={tool ? inkCursor(color) : 'default'}
        // A pixel viewBox rather than a 0–1 one: a unit box would need
        // non-uniform scaling, which distorts round caps and joins.
        viewBox={`0 0 ${rect.width} ${rect.height}`}
        width={rect.width}
        height={rect.height}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {visible.map((stroke, index) => (
          <path
            key={index}
            d={strokePath(stroke, rect)}
            stroke={stroke.color}
            strokeWidth={toLocalLength(stroke.width, rect)}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        ))}
      </Surface>
    </OverlayRoot>,
    document.body,
  );
};

/**
 * A dot cursor in the ink about to be drawn.
 *
 * Answering "can the cursor be the selected colour?" — yes: an inline SVG data
 * URI accepts any fill, so the cursor carries the live ink instead of a generic
 * crosshair. The white outer ring keeps it visible on both bright and dark
 * footage, and the hotspot is the centre of the dot so the mark starts exactly
 * where the dot sits.
 *
 * `#` must be percent-encoded — an unescaped hash truncates the data URI at the
 * colour and the cursor silently falls back. Falls back to `crosshair` anyway if
 * a browser refuses the URI.
 */
export function inkCursor(color: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18">` +
    `<circle cx="9" cy="9" r="5.5" fill="none" stroke="white" stroke-width="2" opacity="0.9"/>` +
    `<circle cx="9" cy="9" r="4" fill="${color}"/>` +
    `</svg>`;
  return `url('data:image/svg+xml;utf8,${svg.replace(/#/g, '%23')}') 9 9, crosshair`;
}

const isSamePoint = (a: NormalizedPoint, b: NormalizedPoint): boolean =>
  a[0] === b[0] && a[1] === b[1];

export default ClipAnnotationLayer;

// Fixed, because the rect is measured in viewport space. Inert by default so
// the canvas underneath keeps every click it has today.
const OverlayRoot = styled.div`
  position: fixed;
  pointer-events: none;
  overflow: hidden;
  z-index: ${CLIP_PREVIEW_Z.annotationOverlay};
`;

const Surface = styled.svg<{ $armed: boolean; $cursor: string }>`
  display: block;
  /* Interactive only while a tool is armed — this is the switch that decides
     whether the canvas or the drawing surface receives the pointer. */
  pointer-events: ${props => (props.$armed ? 'auto' : 'none')};
  cursor: ${props => props.$cursor};
  /* Stop a drag from selecting modal text or starting a native image drag. */
  touch-action: none;
  user-select: none;
`;
